<template>
	<LModal
		v-model:open="isOpen"
		:title="message ? message.Title : ''"
		:width="560"
	>
		<div v-if="message" class="message-detail">
			<div class="meta-row">
				<LTonePill :label="severity.Label" :tone="severity.Tone" />
				<span class="meta-dates">{{ windowLabel }}</span>
			</div>

			<!-- Body text is rendered as text, never as markup: it is authored in the Lotus
				staff console and shown inside a customer's session, so treating it as HTML
				would make the composer a script-injection vector into every account. Blank
				lines become paragraphs, which is all the formatting a notice needs. -->
			<div v-if="bodyParagraphs.length" class="body-text">
				<p v-for="(paragraph, index) in bodyParagraphs" :key="index">{{ paragraph }}</p>
			</div>
			<p v-else class="body-text body-empty">There's nothing more to this message.</p>
		</div>

		<template #footer>
			<LButton v-if="hasLink" variant="secondary" @click="followLink">{{ linkText }}</LButton>
			<LButton v-if="canDismiss" variant="secondary" @click="dismiss">Dismiss</LButton>
			<LButton variant="primary" @click="isOpen = false">Close</LButton>
		</template>
	</LModal>
</template>










<script setup>
/**
 * The full text of one system message, opened from its banner or from the Messages page.
 *
 * The banner carries the one-line title; anything longer lives here. Dismissing is offered
 * from the modal as well as the banner, because a reader who opened a notice to read it is
 * exactly the reader who is then done with it.
 */
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { resolveSystemMessageSeverity } from '../system-messages-display-values.js';
import { formatDisplayMoment } from '/helpers/date-formatting.js';
import { LModal, LButton, LTonePill } from '/components2/index.js';

const router = useRouter();




//-- Props
const props = defineProps({
	message: {
		type: Object,
		default: null,
	},
});




//-- Emits
const emit = defineEmits(['dismiss']);




//-- Data/Reactive State
const isOpen = defineModel('open', { default: false });




//-- Computed Properties
const severity = computed(() => resolveSystemMessageSeverity(props.message ? props.message.SeverityCode : ""));


/**
 * The publication window in the reader's own timezone. The API sends UTC instants; a
 * maintenance window is only meaningful to someone in the hours they actually keep.
 */
const windowLabel = computed(() =>
{
	if (props.message == null) return "";

	const from = formatDisplayMoment(props.message.PublishFromUTC);

	if (!props.message.PublishUntilUTC) return "From " + from;

	return from + " to " + formatDisplayMoment(props.message.PublishUntilUTC);
});


// Blank-line-separated blocks, so a composer's paragraphs survive without the body being
// treated as markup.
const bodyParagraphs = computed(() =>
{
	if (props.message == null) return [];
	if (!props.message.Body) return [];

	return props.message.Body
		.split(/\n\s*\n/)
		.map((paragraph) => paragraph.trim())
		.filter((paragraph) => paragraph != "");
});


const hasLink = computed(() => props.message != null && !!props.message.LinkUrl);


const linkText = computed(() =>
{
	if (props.message == null) return "";

	return props.message.LinkText ? props.message.LinkText : "Take me there";
});


// Already-dismissed messages are re-openable from the Messages page, and there is nothing
// left to dismiss on one of those.
const canDismiss = computed(() => props.message != null && props.message.IsDismissible && !props.message.IsDismissed);




//-- Methods
const dismiss = () =>
{
	emit('dismiss', props.message);

	isOpen.value = false;
};


/**
 * Follow the message's action link. A root-relative path stays inside the SPA (a full
 * page load would throw away the session's loaded state); anything else is a real
 * external destination and opens in its own tab.
 */
const followLink = async () =>
{
	const url = props.message.LinkUrl;

	isOpen.value = false;

	if (url.startsWith("/"))
	{
		await router.push({ path: url });
		return;
	}

	window.open(url, "_blank", "noopener");
};
</script>










<style scoped>
.message-detail
{
	display: flex;
	flex-direction: column;
	gap: var(--sp-4);
}

.meta-row
{
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: var(--sp-3);
}

.meta-dates
{
	font-size: var(--text-chip);
	color: var(--text-3);
}

.body-text
{
	display: flex;
	flex-direction: column;
	gap: var(--sp-3);
	font-size: var(--text-base);
	line-height: 1.6;
	color: var(--text-2);
	white-space: pre-line;
	text-wrap: pretty;
}

.body-empty
{
	color: var(--text-3);
}
</style>
